import {
  Component,
  HostListener,
  Input,
  EventEmitter,
  OnInit,
  Inject,
  Renderer2,
  Output,
  PLATFORM_ID
} from "@angular/core";
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";
import { TranslateService } from '@ngx-translate/core';
import {
  DataService,
  DeviceHelper,
  ToolbarComponent,
  FgMenuService,
  FG_APP_CONSTANTS,
  FgAppConstants,
  ThemingService,
  LocalStorageService,
  DialogLogoutComponent,
  UserService,
} from "fg-library";
import { LayoutService } from "../main-nav/layoutservice";
import { MatDialog } from "@angular/material/dialog";
import { fingressThemes, logout, Theme } from "./utils/toolbarUtils";
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: "fg-client-toolbar",
  templateUrl: "./toolbar.component.html",
  styleUrls: ["./toolbar.component.scss"],
  standalone: false
})
export class FgClientToolbarComponent extends ToolbarComponent implements OnInit {
  @Output() openSettingsRequested = new EventEmitter<void>();
  @Output() sidebarToggle = new EventEmitter<void>();

  currentUserDetails: any;
  userRole: any = {};
  isMobile: boolean;
  hiddenMenu: boolean = false;
  themes: Theme[] = fingressThemes;
  currentTheme: string;
  logo: string;
  showMe: boolean = false;
  isDark = false;
  appDirection: "ltr" | "rtl" = "ltr";
  selectedLanguage: string = 'en';

  constructor(
    public router: Router,
    public dataService: DataService,
    public route: ActivatedRoute,
    public fgMenuService: FgMenuService,
    @Inject(FG_APP_CONSTANTS) private fgAppConstant: FgAppConstants,
    public themingService: ThemingService,
    private renderer: Renderer2,
    private localStorage: LocalStorageService,
    private layoutService: LayoutService,
    public dialog: MatDialog,
    public userService: UserService,
    public translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super(router, dataService, route, fgMenuService);
    this.isMobile = DeviceHelper.isMobile();
    this.currentTheme = this.themes[0].class;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Load direction
      const savedDir = this.localStorage.get("appDirection") as "ltr" | "rtl" || "ltr";
      this.appDirection = savedDir;
      document.body.dir = this.appDirection;

      // Load AppPreference
      let appPreference = this.localStorage.get("AppPreference");
      appPreference = appPreference ? JSON.parse(appPreference) : { theme: "tradeportal-theme", layout: "HORIZONTAL" };

      this.setLayout(appPreference.layout);
      this.switch(appPreference.theme);

      // Load selected language
      this.selectedLanguage = sessionStorage.getItem("appLanguage") || 'en';
    }

    // Load translations
    this.dataService.getLocaleFile(this.selectedLanguage).subscribe((response: any) => {
      this.translate.setDefaultLang(this.selectedLanguage);
      this.translate.setTranslation(this.selectedLanguage, response);

      if (this.fgMenuService?.currentApplicationMenus) {
        this.fgMenuService.currentApplicationMenus = this.updateDisplayNames(
          this.fgMenuService.currentApplicationMenus
        );
      }
    });

    // Subscribe to layout changes
    this.layoutService.layout$.subscribe((layout) => {
      this.setLayout(layout);
    });

    // Subscribe to current user
    this.userService.currentUser.subscribe((response) => {
      this.currentUserDetails = response;
      if (this.currentUserDetails?.lastLoginTime) {
        this.currentUserDetails["lastLoginTime"] = new Date(this.currentUserDetails["lastLoginTime"]);
      }
    });
  }

  triggerSettingsDrawer() {
    this.openSettingsRequested.emit();
  }

  logout(directLogin: boolean = false) {
    logout(directLogin, this.config, this.router, this.fgAppConstant, this.userService, this.dataService);
  }

  openLogoutDialog(): void {
    const logoutDialog = this.dialog.open(DialogLogoutComponent, { width: '300px' });
    logoutDialog.afterClosed().subscribe(result => { if (result) this.logout(); });
  }

  goToLogin() {
    this.userService.logout();
    const session = this.userService.isUserLoggedIn();
    if (!session) this.router.navigate(["/app/login"], { queryParams: { logout: "true" } });
  }

  setLayout(layout: string) {
    if (layout === "VERTICAL") this.showMe = true;
    else this.showMe = false;
  }

  toggleSidebar() {
    this.sidebarToggle.emit();
  }

  toggleDark(checked: boolean) {
    this.isDark = checked;
    if (isPlatformBrowser(this.platformId)) {
      const method = checked ? "addClass" : "removeClass";
      this.renderer[method](document.body, "dark-theme");
    }
  }

  switch(themeClass: string) {
    if (!isPlatformBrowser(this.platformId)) return;

    // Remove old themes
    this.themes.forEach((t) => this.renderer.removeClass(document.body, t.class));

    // Apply new theme
    this.currentTheme = themeClass;
    this.renderer.addClass(document.body, themeClass);
    this.localStorage.set("theme", this.currentTheme);

    this.logo = (themeClass === "dark-theme") ? "assets/img/logo_white.png" : "assets/img/logo.png";
  }

  getCurrentLogo() {
    return this.currentTheme === "dark-theme" ? "assets/img/logo_white.png" : "assets/img/logo.png";
  }

  changeLanguage(lang: string) {
    if (!isPlatformBrowser(this.platformId)) return;

    sessionStorage.setItem('appLanguage', lang);
    if (lang === 'ar') {
      this.appDirection = 'rtl';
      this.localStorage.set("appDirection", 'rtl');
    }
    else{
      this.appDirection ='ltr';
      this.localStorage.set("appDirection",'ltr')
    }
    const savedDir = this.localStorage.get("appDirection") || "ltr";
    this.appDirection = savedDir as "ltr" | "rtl";
    document.body.dir = this.appDirection;

    this.dataService.getLocaleFile(lang).subscribe((response: any) => {
      this.translate.setDefaultLang(lang);
      this.translate.setTranslation(lang, response);
      if (this.fgMenuService?.currentApplicationMenus) {
        this.fgMenuService.currentApplicationMenus = this.updateDisplayNames(
          this.fgMenuService.currentApplicationMenus
        );
      }

      window.location.reload();
    });
  }

  updateDisplayNames(obj: any, basePath?: string): any {
    if (!basePath && this.fgMenuService?.appId) {
      basePath = `apps.${this.fgMenuService.fgCurrentApplication.code}`;
    }

    if (!basePath) basePath = 'apps';

    if (Array.isArray(obj)) {
      return obj.map(item => this.updateDisplayNames(item, basePath));
    }

    if (typeof obj === 'object' && obj !== null) {
      for (const key in obj) {
        if (!obj.hasOwnProperty(key)) continue;
        const value = obj[key];
        const translatableKeys = ['menu_name', 'name', 'displayName', 'title', 'app_name', 'form_name', 'model_name'];

        if (translatableKeys.includes(key) && typeof value === 'string') {
          const tKey = `${basePath}.navigation.messages.${value}`;
          const translated = this.translate.instant(tKey);
          obj[key] = translated && translated !== tKey ? translated : value;
        } else if (typeof value === 'object' && value !== null) {
          obj[key] = this.updateDisplayNames(value, basePath);
        }
      }
    }
    return obj;
  }
}
